import React, { useRef, useCallback } from "react";
import {
  ChevronLeft,
  Download,
  Share2,
  Bookmark,
  CheckCircle2,
  Circle,
  ChevronDown,
} from "lucide-react";
import html2canvas from "html2canvas";
import { useProfile } from "../hooks/useAuth";
import { useRelationshipOfficer } from "../hooks/dashboard/useRelationshipOfficer";
import DashboardFooter from "../pages/dashboard/DashboardFooter";
import Logo from "../assets/img/newlogo.png";
import { useNavigate } from "react-router-dom";
import {
  useBookmarks,
  useBookmarkContent,
  useDeleteBookmark,
} from "../hooks/financialInsights/useFinancialInsights";

// ----- Helper Components -----
const TimelineItem = ({ status, title, date, isLast }) => (
  <div className="relative flex gap-6 pb-8 last:pb-0">
    {!isLast && (
      <div className="absolute left-[7px] top-4 bottom-0 w-[1px] border-l border-dashed border-white/20"></div>
    )}
    <div className="relative z-10 mt-1">
      {status === "completed" && (
        <div className="bg-green-500 rounded-full p-0.5">
          <CheckCircle2 size={12} className="text-[#1F1D2B]" />
        </div>
      )}
      {status === "active" && (
        <div className="w-4 h-4 rounded-full border border-white/40 bg-[#1F1D2B] flex items-center justify-center">
          <div className="w-1.5 h-1.5 rounded-full bg-white/40"></div>
        </div>
      )}
      {status === "pending" && <Circle size={16} className="text-white/20" />}
    </div>
    <div>
      <h5
        className={`text-xs font-bold ${status === "pending" ? "text-slate-500" : "text-white"}`}
      >
        {title}
      </h5>
      <p className="text-[10px] text-slate-400 mt-1">{date}</p>
    </div>
  </div>
);

const CollapsedClaim = ({ title, refId, status }) => (
  <div className="bg-[#2D2B3F]/40 p-5 rounded-2xl flex justify-between items-center hover:bg-[#2D2B3F]/60 transition-colors cursor-pointer mb-4">
    <div>
      <h4 className="text-sm font-bold text-slate-200">{title}</h4>
      <p className="text-[10px] text-slate-500 uppercase font-mono mt-0.5">
        Ref: {refId}
      </p>
    </div>
    <div className="flex items-center gap-2">
      <span className="text-[10px] text-green-500 font-bold">{status}</span>
      <ChevronDown size={14} className="text-slate-400" />
    </div>
  </div>
);

// ----- Main Component -----
const AffluentCardScreen = ({ onBack }) => {
  const navigate = useNavigate();
  const cardRef = useRef(null);
  const { data: userData, isLoading: isLoadingUser } = useProfile();
  const { data: officerData } = useRelationshipOfficer();
  const { data: bookmarks, isLoading: isLoadingBookmarks } = useBookmarks();
  const bookmarkContent = useBookmarkContent();
  const deleteBookmark = useDeleteBookmark();
  // UI-only toggle: keep bookmark/saved-content logic intact, but hide the section on this screen.
  const SHOW_SAVED_CONTENT = false;

  const userName = userData?.data?.name || "Member";
  const ghanaCardNumber = userData?.data?.ghana_card_number || "";
  const officerName =
    officerData?.agent_name ||
    officerData?.fullName ||
    "Your Relationship Officer";
  const memberSince = userData?.data?.date_joined
    ? new Date(userData.data.date_joined.replace(" ", "T")).toLocaleDateString(
        "en-US",
        { month: "short", year: "numeric" },
      )
    : "";

  const handleBack = () => {
    navigate("/dashboard");
  };

  const handleBookmarkToggle = (contentId, isBookmarked) => {
    if (isBookmarked) {
      deleteBookmark.mutate({ id: contentId });
    } else {
      bookmarkContent.mutate({ contentId });
    }
  };

  const captureCardAsImage = useCallback(async () => {
    if (!cardRef.current) {
      alert("Card element not found");
      return null;
    }
    try {
      const canvas = await html2canvas(cardRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: null,
      });

      const radius = 64;
      const roundedCanvas = document.createElement("canvas");
      const ctx = roundedCanvas.getContext("2d");

      roundedCanvas.width = canvas.width;
      roundedCanvas.height = canvas.height;

      // Create rounded rectangle clip path
      ctx.beginPath();
      ctx.moveTo(radius, 0);
      ctx.lineTo(canvas.width - radius, 0);
      ctx.quadraticCurveTo(canvas.width, 0, canvas.width, radius);
      ctx.lineTo(canvas.width, canvas.height - radius);
      ctx.quadraticCurveTo(
        canvas.width,
        canvas.height,
        canvas.width - radius,
        canvas.height,
      );
      ctx.lineTo(radius, canvas.height);
      ctx.quadraticCurveTo(0, canvas.height, 0, canvas.height - radius);
      ctx.lineTo(0, radius);
      ctx.quadraticCurveTo(0, 0, radius, 0);
      ctx.closePath();

      // Clip first, then draw - corners will be transparent
      ctx.clip();
      ctx.drawImage(canvas, 0, 0);

      return roundedCanvas;
    } catch (err) {
      alert("Failed to capture card: " + err.message);
      return null;
    }
  }, []);

  const handleDownload = useCallback(async () => {
    try {
      const canvas = await captureCardAsImage();
      if (!canvas) return;
      const link = document.createElement("a");
      link.download = `affluent-card-${userName.replace(/\s+/g, "-").toLowerCase()}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (error) {
      alert("Download failed: " + error.message);
    }
  }, [captureCardAsImage, userName]);

  const handleShare = useCallback(async () => {
    try {
      const canvas = await captureCardAsImage();
      if (!canvas) return;
      canvas.toBlob(async (blob) => {
        if (!blob) {
          alert("Failed to create image blob");
          return;
        }
        const file = new File(
          [blob],
          `affluent-card-${userName.replace(/\s+/g, "-").toLowerCase()}.png`,
          { type: "image/png" },
        );
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({
            title: "Legacy Card",
            text: "Check out my Legacy Member Card",
            files: [file],
          });
        } else {
          const link = document.createElement("a");
          link.download = file.name;
          link.href = URL.createObjectURL(blob);
          link.click();
          URL.revokeObjectURL(link.href);
        }
      }, "image/png");
    } catch (error) {
      alert("Share failed: " + error.message);
    }
  }, [captureCardAsImage, userName]);

  if (isLoadingUser) {
    return (
      <div className="min-h-screen bg-[#1F1D2B] flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-green-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1F1D2B] text-white font-sans flex flex-col relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')]"></div>
      </div>

      {/* Top Navigation */}
      <div className="sticky top-0 z-10 px-4 py-3 flex items-center">
        <button
          onClick={handleBack}
          className="p-2 hover:bg-white/10 rounded-full transition"
          aria-label="Go back"
        >
          <ChevronLeft size={24} />
        </button>
        <h1 className="ml-3 text-lg font-semibold">Legacy Card</h1>
      </div>

      <main className="flex-1 max-w-6xl mx-auto w-full px-6 pt-6 pb-24 z-10">
        {/* Card Display Section */}
        <section className="flex flex-col items-center mb-12">
          <div
            ref={cardRef}
            className="w-full max-w-[760px] rounded-[2.25rem] px-10 py-16 shadow-2xl relative overflow-hidden
                       bg-gradient-to-br from-[#2F2B3F] to-[#1F1D2B]"
          >
            {/* Top */}
            <div className="flex justify-between items-start">
              <div>
                <p className="text-[22px] font-extrabold tracking-[0.25em] text-white/95 uppercase">
                  LEGACY MEMBER CARD
                </p>
                <h2 className="text-[34px] font-medium mt-7 tracking-[0.06em] text-white/95 uppercase">
                  {userName}
                </h2>
                <p className="text-[28px] font-medium mt-6 text-[#16a34a] tracking-[0.02em]">
                  Ghana Card No: {ghanaCardNumber}
                </p>
              </div>

              <div className="-mt-2">
                <img
                  src={Logo}
                  alt="Logo"
                  className="w-28 h-28 object-contain opacity-95"
                />
              </div>
            </div>

            {/* Divider */}
            <div className="h-px bg-white/15 my-14" />

            {/* Bottom */}
            <div className="flex justify-between items-end">
              <div>
                <p className="text-[26px] font-medium text-white/85">
                  Member Since
                </p>
                <p className="text-[34px] font-medium mt-3 text-[#16a34a]">
                  {memberSince || "N/A"}
                </p>
              </div>
              <div className="text-left">
                <p className="text-[26px] font-medium text-white/85">Your RO</p>
                <p className="text-[34px] font-medium mt-3 text-[#16a34a]">
                  {officerName}
                </p>
              </div>
            </div>
          </div>

          {/* Carousel Indicators */}
          <div className="flex gap-2 mt-8 items-center">
            <div className="w-2.5 h-2.5 rounded-full bg-green-500 border-2 border-[#1F1D2B]"></div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 mt-8">
            <button
              onClick={handleDownload}
              className="bg-[#10B981] hover:bg-[#059669] text-white px-8 py-3 rounded-full text-xs font-bold flex items-center gap-2 transition-all shadow-lg cursor-pointer"
            >
              <Download size={16} /> DOWNLOAD CARD
            </button>
            <button
              onClick={handleShare}
              className="bg-transparent border border-white/40 hover:bg-white/5 text-white px-8 py-3 rounded-full text-xs font-bold flex items-center gap-2 transition-all cursor-pointer"
            >
              <Share2 size={16} /> SHARE
            </button>
          </div>
        </section>

        {/* Saved Content Section - hidden for now (UI only) */}
        {SHOW_SAVED_CONTENT && (
          <section className="mb-12">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-sm font-bold">Saved Content</h3>
              <button className="text-xs text-green-500 font-bold">
                see all
              </button>
            </div>
            <p className="text-[11px] text-slate-500 mb-4 tracking-tight">
              Your bookmarked articles, videos, and infographics
            </p>

            {isLoadingBookmarks ? (
              <div className="bg-[#2D2B3F]/30 p-6 rounded-[1.5rem] flex justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-green-500"></div>
              </div>
            ) : bookmarks?.length > 0 ? (
              <div className="space-y-4">
                {bookmarks.map((bookmark) => (
                  <div
                    key={bookmark.id}
                    className="bg-[#2D2B3F]/30 p-6 rounded-[1.5rem]"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-[10px] text-slate-400">
                        {bookmark.content_type || "Article"} •{" "}
                        {bookmark.read_time || "6 mins read"}
                      </span>
                      <button
                        onClick={() =>
                          handleBookmarkToggle(bookmark.content_id, true)
                        }
                        className="text-white hover:text-green-500 transition-colors"
                      >
                        <Bookmark size={16} className="fill-white text-white" />
                      </button>
                    </div>
                    <h4 className="text-sm font-bold mb-2">{bookmark.title}</h4>
                    <p className="text-xs text-slate-400 leading-relaxed">
                      {bookmark.excerpt || "No description available"}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-[#2D2B3F]/30 p-6 rounded-[1.5rem] text-center">
                <p className="text-xs text-slate-400">No saved content yet</p>
              </div>
            )}
          </section>
        )}

        {/* Track Claims Section - hidden for now */}
        {false && (
          <section>
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-sm font-bold">Track Claims</h3>
              <button className="text-xs text-green-500 font-bold">
                see all
              </button>
            </div>
            <p className="text-[11px] text-slate-500 mb-6 tracking-tight">
              Tap any request to view timeline details
            </p>

            {/* Expanded Claim */}
            <div className="bg-[#2D2B3F]/30 rounded-[1.5rem] mb-4">
              <div className="p-6 flex justify-between items-center">
                <div>
                  <h4 className="text-sm font-bold text-slate-200">
                    Medical Claim
                  </h4>
                  <p className="text-[10px] text-slate-500 uppercase font-mono mt-0.5">
                    Ref: CLM-2025-0001142
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-green-500 font-bold">
                    Review Ongoing
                  </span>
                  <ChevronDown size={16} className="text-slate-400" />
                </div>
              </div>

              <div className="px-12 pb-10 pt-4">
                <TimelineItem
                  status="completed"
                  title="Request Received"
                  date="Jan 2, 2026 • 10:30 AM"
                />
                <TimelineItem
                  status="active"
                  title="Review Ongoing"
                  date="In Progress • Est. 2-3 business days"
                />
                <TimelineItem
                  status="pending"
                  title="Payment Processing"
                  date="Jan 3, 2026 • 10:30 AM"
                />
                <TimelineItem
                  status="pending"
                  title="Claim Paid"
                  date="Pending"
                  isLast
                />
              </div>
            </div>

            <CollapsedClaim
              title="Policy Update"
              refId="CLM-2025-0001142"
              status="Completed"
            />
            <CollapsedClaim
              title="Document Request"
              refId="DOC-2025-00009038"
              status="Completed"
            />
          </section>
        )}
      </main>

      <DashboardFooter />
    </div>
  );
};

export default AffluentCardScreen;
